/* ASSET - assets/collection-grid.js */

/* --------------------------------------------------------------------------
   Loaded after facets.js (both defer, declaration order in
   sections/collection-grid.liquid is what guarantees it).

   Two independent pieces:

   1. CocoGridWatcher - watches #ProductGridContainer and re-initialises Swym
      after every facets render. facets.js:81 replaces that container's
      innerHTML, which destroys the injected wishlist buttons and leaves Swym
      unaware. We dispatch swym:collections-loaded and call
      initializeActionButtons on the grid parent. This is bootstrapped from the
      section wrapper, NOT from the toolbar, because the Toolbar block is
      optional and filtering must keep working without it.
      It also owns the drawer shims (bindDrawer) and the drawer count mirror
      (bindCount), for the same reason: neither may depend on the Toolbar block.

   2. CocoCollectionToolbar - the optional Toolbar block. Filter button, cycling
      Sort button, desktop layout switch.
      Sorting deliberately avoids a second form: facets.js:240 merges forms by
      id, so a second sort form would produce duplicate sort_by params. Instead
      we read window.location.search, swap sort_by, and hand the string to
      FacetFiltersForm.renderPage. That also fixes the stock Dawn bug where
      sorting in drawer mode submits only #FacetSortDrawerForm and drops every
      active filter.
      The layout switch is not persisted - it resets to the merchant's
      "Products per row on desktop" setting on every page load.
   -------------------------------------------------------------------------- */

class CocoGridWatcher {
  static EVENT = 'swym:collections-loaded'
  static swymBound = false

  static bindSwym() {
    if (CocoGridWatcher.swymBound) return
    CocoGridWatcher.swymBound = true

    window.SwymCallbacks = window.SwymCallbacks || []
    window.SwymCallbacks.push(function (swat) {
      document.addEventListener(CocoGridWatcher.EVENT, function () {
        const api = swat && swat.advancedCollectionsApi
        if (api && typeof api.reinitialize === 'function') api.reinitialize()
      })
    })
  }

  static attach(collection) {
    CocoGridWatcher.bindDrawer(collection)
    CocoGridWatcher.bindCount(collection)

    const container = collection.querySelector('#ProductGridContainer')
    if (!container || container.dataset.cocoWatched === 'true') return

    container.dataset.cocoWatched = 'true'
    const selector = collection.dataset.gridSelector || '#product-grid'

    CocoGridWatcher.bindSwym()
    return new CocoGridWatcher(container, selector)
  }

  static bindDrawer(collection) {
    const main = collection.querySelector('[data-coco-facets-main]')

    if (main && main.dataset.cocoShim !== 'true') {
      main.dataset.cocoShim = 'true'
      main.addEventListener(
        'click',
        (event) => {
          if (event.target.closest('summary')) event.stopPropagation()
        },
        true
      )
    }

    collection.querySelectorAll('[data-coco-drawer-close]').forEach((button) => {
      if (button.dataset.cocoBound === 'true') return
      button.dataset.cocoBound = 'true'
      button.addEventListener('click', () => CocoGridWatcher.closeDrawer(collection))
    })
  }

  static closeDrawer(collection) {
    const summary = collection.querySelector('.mobile-facets__wrapper > details > summary')
    if (summary) summary.click()
  }

  static bindCount(collection) {
    const source = collection.querySelector('#ProductCount')
    if (!source || source.dataset.cocoMirrored === 'true') return

    const mirrors = collection.querySelectorAll('[data-coco-count-mirror]')
    if (!mirrors.length) return

    source.dataset.cocoMirrored = 'true'

    const sync = () => {
      mirrors.forEach((node) => {
        node.innerHTML = source.innerHTML
      })
    }

    sync()
    new MutationObserver(sync).observe(source, {
      childList: true,
      characterData: true,
      subtree: true
    })
  }

  constructor(container, gridParentSelector) {
    this.container = container
    this.gridParentSelector = gridParentSelector
    this.pending = false
    this.observer = new MutationObserver(this.onMutate.bind(this))
    this.observer.observe(this.container, { childList: true })
  }

  onMutate() {
    if (this.pending) return
    this.pending = true

    window.requestAnimationFrame(() => {
      this.pending = false
      document.dispatchEvent(
        new CustomEvent(CocoGridWatcher.EVENT, {
          bubbles: true,
          detail: { selector: this.gridParentSelector }
        })
      )
    })
  }

  disconnect() {
    this.observer.disconnect()
  }
}

class CocoCollectionToolbar extends HTMLElement {
  connectedCallback() {
    this.collection = this.closest('[data-coco-collection]')
    if (!this.collection) return

    this.filterButton = this.querySelector('[data-coco-filter-open]')
    this.sortButton = this.querySelector('[data-coco-sort]')
    this.sortLabel = this.querySelector('[data-coco-sort-label]')
    this.layoutButtons = Array.from(this.querySelectorAll('[data-coco-layout]'))
    this.sortOptions = this.parseSortOptions()

    this.onFilterClick = this.onFilterClick.bind(this)
    this.onSortClick = this.onSortClick.bind(this)
    this.onLayoutClick = this.onLayoutClick.bind(this)
    this.onPopState = this.onPopState.bind(this)

    if (this.filterButton) this.filterButton.addEventListener('click', this.onFilterClick)
    if (this.sortButton) this.sortButton.addEventListener('click', this.onSortClick)
    this.layoutButtons.forEach((button) => button.addEventListener('click', this.onLayoutClick))
    window.addEventListener('popstate', this.onPopState)

    this.resetColumns()
  }

  disconnectedCallback() {
    if (this.filterButton) this.filterButton.removeEventListener('click', this.onFilterClick)
    if (this.sortButton) this.sortButton.removeEventListener('click', this.onSortClick)
    this.layoutButtons.forEach((button) => button.removeEventListener('click', this.onLayoutClick))
    window.removeEventListener('popstate', this.onPopState)
  }

  parseSortOptions() {
    if (!this.sortButton) return []

    try {
      const parsed = JSON.parse(this.sortButton.dataset.sortOptions || '[]')
      return Array.isArray(parsed) ? parsed : []
    } catch (error) {
      return []
    }
  }

  onFilterClick(event) {
    event.preventDefault()
    CocoGridWatcher.closeDrawer(this.collection)
  }

  /* Sort ---------------------------------------------------------------- */

  currentSortValue() {
    const params = new URLSearchParams(window.location.search)
    const fromUrl = params.get('sort_by')
    if (fromUrl) return fromUrl

    return this.sortButton ? this.sortButton.dataset.defaultSort || '' : ''
  }

  onSortClick(event) {
    event.preventDefault()
    if (!this.sortOptions.length) return

    const params = new URLSearchParams(window.location.search)
    const current = this.currentSortValue()
    let index = this.sortOptions.findIndex((option) => option.value === current)
    if (index < 0) index = 0

    const next = this.sortOptions[(index + 1) % this.sortOptions.length]
    params.set('sort_by', next.value)
    params.delete('page')

    this.setSortLabel(next.name)

    const facetFiltersForm = customElements.get('facet-filters-form')
    const drawerForm = this.collection.querySelector('#FacetFiltersFormMobile')

    if (!drawerForm || !facetFiltersForm || typeof facetFiltersForm.renderPage !== 'function') {
      window.location.search = params.toString()
      return
    }

    facetFiltersForm.renderPage(params.toString())
  }

  setSortLabel(name) {
    if (this.sortLabel) this.sortLabel.textContent = name
  }

  syncSortLabel() {
    if (!this.sortOptions.length) return

    const current = this.currentSortValue()
    const match = this.sortOptions.find((option) => option.value === current)
    this.setSortLabel(match ? match.name : this.sortOptions[0].name)
  }

  onPopState() {
    this.syncSortLabel()
  }

  /* Layout switch -------------------------------------------------------- */

  resetColumns() {
    if (!this.layoutButtons.length) return
    this.applyColumns(this.collection.dataset.cocoColsDefault || this.layoutButtons[0].dataset.cocoLayout)
  }

  onLayoutClick(event) {
    this.applyColumns(event.currentTarget.dataset.cocoLayout)
  }

  applyColumns(value) {
    if (!value) return

    this.collection.setAttribute('data-coco-cols', value)

    this.layoutButtons.forEach((button) => {
      button.setAttribute('aria-pressed', button.dataset.cocoLayout === value ? 'true' : 'false')
    })
  }
}

if (!customElements.get('coco-collection-toolbar')) {
  customElements.define('coco-collection-toolbar', CocoCollectionToolbar)
}

function cocoInitCollectionGrids(root) {
  const scope = root || document
  scope.querySelectorAll('[data-coco-collection]').forEach((collection) => {
    CocoGridWatcher.attach(collection)
  })
}

cocoInitCollectionGrids()

document.addEventListener('shopify:section:load', (event) => {
  cocoInitCollectionGrids(event.target)
})
